import { useMemo, useState } from "react";
import { topicNodes } from "../data";

const kindLabels = {
  core: "Program outcome",
  evidence: "Evidence system",
  delivery: "Delivery system",
  risk: "Risk control",
  finance: "Finance and scale",
};

type MapNode = {
  id: string;
  eyebrow: string;
  title: string;
  x: number;
  y: number;
  w: number;
  h: number;
  group: "core" | "evidence" | "control" | "delivery" | "adoption";
};

const mapNodes: MapNode[] = [
  { id: "access", eyebrow: "Core model", title: "Inclusive language access", x: 50, y: 54, w: 22, h: 19, group: "core" },
  { id: "sources", eyebrow: "Evidence", title: "Approved source base", x: 39, y: 13, w: 19, h: 13, group: "evidence" },
  { id: "languages", eyebrow: "Portfolio", title: "Language portfolio", x: 16, y: 34, w: 19, h: 13, group: "delivery" },
  { id: "mel", eyebrow: "Learning", title: "MEL and learning", x: 17, y: 69, w: 19, h: 13, group: "evidence" },
  { id: "cost", eyebrow: "Economics", title: "Cost model and sustainability", x: 48, y: 85, w: 21, h: 14, group: "adoption" },
  { id: "partners", eyebrow: "Ecosystem", title: "Partners and ecosystem", x: 70, y: 86, w: 20, h: 13, group: "delivery" },
  { id: "policy", eyebrow: "Scale", title: "Policy adoption", x: 87, y: 66, w: 18, h: 13, group: "adoption" },
  { id: "safeguards", eyebrow: "Safeguards", title: "Safeguards and ethics", x: 86, y: 49, w: 18, h: 13, group: "control" },
  { id: "service", eyebrow: "Delivery", title: "Service journeys", x: 82, y: 25, w: 18, h: 13, group: "delivery" },
  { id: "qa", eyebrow: "Controls", title: "QA and release gates", x: 62, y: 31, w: 18, h: 13, group: "control" },
];

type MapRelationship = {
  source: string;
  target: string;
  label: string;
  tier: "core" | "context";
  kind: "evidence" | "control" | "delivery" | "feedback" | "scale";
  curve?: number;
};

const mapRelationships: MapRelationship[] = [
  { source: "sources", target: "access", label: "approved evidence grounds the core model", tier: "core", kind: "evidence", curve: -2 },
  { source: "languages", target: "access", label: "validated language assets power the core model", tier: "core", kind: "delivery", curve: 0 },
  { source: "qa", target: "access", label: "release gates authorize the core model", tier: "core", kind: "control", curve: 0 },
  { source: "service", target: "access", label: "service journeys define core user tasks", tier: "core", kind: "delivery", curve: 0 },
  { source: "safeguards", target: "access", label: "safe-answer boundaries constrain the core model", tier: "core", kind: "control", curve: 0 },
  { source: "partners", target: "access", label: "country teams operate the core model", tier: "core", kind: "delivery", curve: 0 },
  { source: "cost", target: "access", label: "sustainability constraints shape scale-ready access", tier: "core", kind: "scale", curve: 0 },
  { source: "access", target: "mel", label: "core usage generates learning evidence", tier: "core", kind: "feedback", curve: 0 },
  { source: "access", target: "policy", label: "core evidence supports policy adoption", tier: "core", kind: "scale", curve: 0 },
  { source: "sources", target: "languages", label: "source terms shape language packs", tier: "context", kind: "evidence", curve: -3 },
  { source: "sources", target: "service", label: "approved sources define service metadata", tier: "context", kind: "evidence", curve: 3 },
  { source: "sources", target: "qa", label: "source grounding enters release checks", tier: "context", kind: "evidence", curve: 0 },
  { source: "service", target: "safeguards", label: "service scope is checked for risk", tier: "context", kind: "control", curve: 0 },
  { source: "safeguards", target: "qa", label: "risk rules constrain release decisions", tier: "context", kind: "control", curve: 0 },
  { source: "mel", target: "qa", label: "learning updates release gates", tier: "context", kind: "feedback", curve: 1 },
  { source: "mel", target: "cost", label: "evidence updates cost assumptions", tier: "context", kind: "feedback", curve: 0 },
  { source: "partners", target: "languages", label: "field teams validate local usage", tier: "context", kind: "delivery", curve: -3 },
  { source: "partners", target: "service", label: "partners verify delivery pathways", tier: "context", kind: "delivery", curve: 3 },
  { source: "cost", target: "policy", label: "unit economics support scale decisions", tier: "context", kind: "scale", curve: 0 },
  { source: "partners", target: "policy", label: "institutional channels support adoption", tier: "context", kind: "scale", curve: -2 },
];

const groupLabels = {
  core: "Central outcome",
  evidence: "Evidence and learning",
  control: "Governance and safety",
  delivery: "Delivery system",
  adoption: "Scale economics",
};

const relationshipNotes: Record<string, string> = {
  access: "The core model integrates approved evidence, language validation, release gates, service journeys, safeguards, country teams, sustainability evidence, learning and policy uptake.",
  languages: "Language portfolio decisions become useful only when they feed the core access model, where validated terms and comprehension checks shape public-service answers.",
  sources: "Approved sources are the grounding layer for the core model; they also prepare language packs, service metadata and QA tests before release.",
  service: "Service journeys define what the core model must help users navigate, then pass through safeguards before public release.",
  safeguards: "Safeguards are applied around the core model, setting safe-answer boundaries and feeding release controls before any public-service pathway scales.",
  qa: "Quality assurance is the release gate into the core model, combining source grounding, language validation, reviewer adjudication and field learning.",
  mel: "Monitoring, evaluation and learning receives evidence from core use and sends improvements back into QA, cost assumptions and future releases.",
  cost: "The cost model determines whether the core access model can scale responsibly across languages, validation intensity and partner channels.",
  partners: "The ecosystem operates around the core model through country leadership, field expertise, validators, language review and adoption channels.",
  policy: "Policy adoption is built from evidence produced by the core model and reinforced by cost and partner readiness.",
};

function nodeEdgePoint(node: MapNode, other: MapNode, offset = 1.3) {
  const dx = other.x - node.x;
  const dy = other.y - node.y;
  const halfW = node.w / 2;
  const halfH = node.h / 2;
  const scaleX = dx === 0 ? Number.POSITIVE_INFINITY : halfW / Math.abs(dx);
  const scaleY = dy === 0 ? Number.POSITIVE_INFINITY : halfH / Math.abs(dy);
  const scale = Math.min(scaleX, scaleY);
  const length = Math.max(1, Math.hypot(dx, dy));
  return {
    x: node.x + dx * scale + (dx / length) * offset,
    y: node.y + dy * scale + (dy / length) * offset,
  };
}

function edgePath(source: MapNode, target: MapNode, curve = 0) {
  const start = nodeEdgePoint(source, target);
  const end = nodeEdgePoint(target, source);
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.max(1, Math.hypot(dx, dy));
  const normalX = -dy / length;
  const normalY = dx / length;
  const bend = Math.abs(curve) < 0.1 || length < 18 ? 0 : curve;
  if (bend === 0) return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} L ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
  const c1 = {
    x: start.x + dx * 0.38 + normalX * bend,
    y: start.y + dy * 0.38 + normalY * bend,
  };
  const c2 = {
    x: start.x + dx * 0.68 + normalX * bend,
    y: start.y + dy * 0.68 + normalY * bend,
  };
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} C ${c1.x.toFixed(2)} ${c1.y.toFixed(2)}, ${c2.x.toFixed(2)} ${c2.y.toFixed(2)}, ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
}

export default function RelationshipMap() {
  const [activeId, setActiveId] = useState("access");
  const activeTopic = topicNodes.find((node) => node.id === activeId) ?? topicNodes[0];
  const activeMapNode = mapNodes.find((node) => node.id === activeId) ?? mapNodes[0];
  const orderedRelationships = useMemo(
    () => [
      ...mapRelationships.filter((relationship) => relationship.tier === "context"),
      ...mapRelationships.filter((relationship) => relationship.tier === "core"),
    ],
    [],
  );
  const connected = useMemo(() => {
    const ids = new Set<string>([activeId]);
    if (activeId !== "access") ids.add("access");
    mapRelationships.forEach(({ source, target }) => {
      if (source === activeId) ids.add(target);
      if (target === activeId) ids.add(source);
    });
    return ids;
  }, [activeId]);

  const activeConnections = mapRelationships
    .filter(({ source, target }) => source === activeId || target === activeId)
    .map((relationship) => {
      const otherId = relationship.source === activeId ? relationship.target : relationship.source;
      const node = mapNodes.find((item) => item.id === otherId);
      return node ? { relationship, node } : null;
    })
    .filter((item): item is { relationship: MapRelationship; node: MapNode } => Boolean(item))
    .sort((a, b) => Number(b.relationship.tier === "core") - Number(a.relationship.tier === "core"));

  return (
    <section id="network" className="section relationship-section">
      <div className="section-heading">
        <div>
          <span className="section-index">02</span>
          <h2>Thematic Relationship Map</h2>
        </div>
        <p>Evidence, governance, product delivery and adoption pathways are shown as an operating graph so reviewers can follow how program inputs become validated public-service access.</p>
      </div>

      <div className="relationship-grid">
        <div className="network-map-panel intelligence-map-panel">
          <div className="map-ring-labels" aria-hidden="true">
            <span>Inputs</span>
            <span>Controls</span>
            <span>Delivery</span>
            <span>Adoption</span>
          </div>
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="intelligence-connectors" aria-hidden="true">
            <defs>
              <radialGradient id="topic-core-glow" cx="50%" cy="54%" r="48%">
                <stop offset="0%" stopColor="rgba(72, 213, 198, 0.3)" />
                <stop offset="55%" stopColor="rgba(72, 213, 198, 0.08)" />
                <stop offset="100%" stopColor="rgba(72, 213, 198, 0)" />
              </radialGradient>
              <marker id="edge-arrow-active" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
                <path d="M0,0 L7,3.5 L0,7 Z" />
              </marker>
            </defs>
            <circle cx="50" cy="54" r="30" className="topic-ring ring-inner" />
            <ellipse cx="50" cy="54" rx="44" ry="36" className="topic-ring ring-outer" />
            <circle cx="50" cy="54" r="26" fill="url(#topic-core-glow)" />
            {orderedRelationships.map((relationship, index) => {
              const source = mapNodes.find((node) => node.id === relationship.source)!;
              const target = mapNodes.find((node) => node.id === relationship.target)!;
              const isActive = relationship.source === activeId || relationship.target === activeId;
              const isNeighbor = connected.has(relationship.source) && connected.has(relationship.target);
              return (
                <path
                  key={`${relationship.source}-${relationship.target}`}
                  d={edgePath(source, target, relationship.curve)}
                  className={`intelligence-edge tier-${relationship.tier} edge-${relationship.kind} ${isActive ? "active" : ""} ${isNeighbor ? "neighbor" : ""}`}
                  markerEnd={isActive ? "url(#edge-arrow-active)" : undefined}
                  style={{ animationDelay: `${index * 80}ms` }}
                />
              );
            })}
          </svg>
          {mapNodes.map((node) => {
            const topic = topicNodes.find((item) => item.id === node.id);
            const isActive = node.id === activeId;
            const isConnected = connected.has(node.id);
            return (
              <button
                key={node.id}
                type="button"
                aria-label={topic?.label ?? node.title}
                className={`orbit-node node-${topic?.kind ?? "delivery"} group-${node.group} ${isActive ? "active" : ""} ${isConnected ? "connected" : "dimmed"}`}
                style={{ left: `${node.x}%`, top: `${node.y}%`, width: `${node.w}%` }}
                onMouseEnter={() => setActiveId(node.id)}
                onFocus={() => setActiveId(node.id)}
                onClick={() => setActiveId(node.id)}
              >
                <span>{node.eyebrow}</span>
                <strong>{node.title}</strong>
              </button>
            );
          })}
          <div className="map-relationship-key" aria-hidden="true">
            <span><i /> Active path</span>
            <span><i /> Core relationship</span>
            <span><i /> Supporting context</span>
          </div>
        </div>

        <aside className="relationship-inspector">
          <span>{kindLabels[activeTopic.kind]}</span>
          <h3>{activeTopic.label}</h3>
          <p>{activeTopic.detail}</p>
          <div className="relationship-position">
            <i>{groupLabels[activeMapNode.group]}</i>
            <b>{activeMapNode.eyebrow}</b>
          </div>
          <div className="relationship-note">
            <strong>Relationship logic</strong>
            <p>{relationshipNotes[activeId]}</p>
          </div>
          <div className="connection-list">
            {activeConnections.map(({ relationship, node }) => {
              const topic = topicNodes.find((item) => item.id === node.id)!;
              const direction = relationship.tier === "core"
                ? "Core relationship"
                : relationship.source === activeId ? "Sends to" : "Receives from";
              return (
                <button key={node.id} type="button" onClick={() => setActiveId(node.id)}>
                  <span>{node.title}</span>
                  <i>{direction}: {relationship.label}</i>
                  <b>{kindLabels[topic.kind]}</b>
                </button>
              );
            })}
          </div>
        </aside>
      </div>
    </section>
  );
}
